/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AnyValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  Button,
  ButtonList,
  ICON_ADD,
  ICON_ARROW_CIRCLE_RIGHT,
  ICON_DELETE,
  ICON_VPN_KEY,
  SPINNER_TYPE_REGULAR,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  Spinner,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  Table,
  TableCell,
  TextField,
  Toast,
  ToastProps,
} from '@squiz/sds';
import {
  ConsoleInstanceType,
  EntityConsoleDatastoreInstance,
  useInstances,
} from 'apps/console-core/src/Services/console';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import every from 'lodash/every';
import has from 'lodash/has';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import set from 'lodash/set';
import some from 'lodash/some';
import React, { SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { getInsightsService, useInsights } from '../../../../Services/insights';
import { DatastoreCollectionValidator } from '../../../../Utilities/DatastoreCollectionValidator/DatastoreCollectionValidator';
import { RegExpValidator } from '../../../../Utilities/RegExpValidator/RegExpValidator';
import { UniqueValueValidator } from '../../../../Utilities/UniqueValueValidator/UniqueValueValidator';
import {
  EVENT_BUILDER_DATA_FORM_IDS,
  EVENT_BUILDER_DATA_IDS,
  EVENT_BUILDER_FORM_ID_PROPERTY_MAP,
  EVENT_BUILDER_TABLE_ID,
} from '../../EventBuilder.constants';
import { EventBuilderDatastoreFormProps } from './EventBuilderDatastoreForm.props';
import { EventBuilderError, EventBuilderFieldValidationResponse } from './EventBuilderDatastoreForm.refs';

/**
 * Generate an attribute id.
 */
function generateAttributeId(): string {
  return Math.random().toString(36).substring(2, 10);
}

/**
 * The create and update a datastore event form.
 */
export const EventBuilderDatastoreForm = (props: EventBuilderDatastoreFormProps): JSX.Element => {
  const { event, resetChanges, setEvent } = props;
  const [attributes, setAttributes] = useState<Array<Array<string>>>(
    // @ts-ignore.
    Object.entries(event.properties).map((entry) => entry.concat([generateAttributeId()])),
  );
  const { t } = useTranslation();
  const [errors, setErrors] = useState<EventBuilderError>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { eventId } = useParams();
  const dataServiceSelectRef = useRef(null);
  const { updateActiveToast } = useToastStore();
  const navigate = useNavigate();
  const isCreate: boolean = useMemo((): boolean => {
    return isUndefined(eventId);
  }, [eventId]);

  // Insights service
  const insightsService = getInsightsService();

  // Existing insights.
  const insightsList = useInsights();
  const otherDatastoreInsights = useMemo((): Array<string> => {
    if (insightsList) {
      return Object.entries(insightsList)
        .filter(([insightId, insight]) => {
          return insightId !== eventId && insight.eventType === `datastore`;
        })
        .map(([, insight]) => {
          return `${insight.filter.datastoreURL}${insight.filter.collectionPrefix}`;
        });
    }
    return [];
  }, [eventId, insightsList]);

  const labels = useMemo(() => {
    return {
      addAttribute: t(CDP_STRINGS.addAttribute),
      addEvent: t(CDP_STRINGS.eventAddTitle),
      attributeMappingDescription: t(CDP_STRINGS.attributeMappingTableDescription),
      attributePlaceholder: t(CDP_STRINGS.attributeEnter),
      attributeTextfieldFrom: t(CDP_STRINGS.attributeTextfieldFrom),
      attributeTextfieldTo: t(CDP_STRINGS.attributeTextfieldTo),
      cancel: t(CDP_STRINGS.cancel),
      collectionLabel: t(CDP_STRINGS.eventBuilderCollectionNameLabel),
      collectionNote: t(CDP_STRINGS.eventBuilderCollectionNameNote),
      collectionPlaceholder: t(CDP_STRINGS.eventBuilderCollectionNamePlaceholder),
      dataIntegration: t(CDP_STRINGS.dataIntegration),
      dataServiceSelectLabel: t(CDP_STRINGS.eventBuilderDataServiceLabel),
      dataServiceSelectPlaceholder: t(CDP_STRINGS.eventBuilderDataServicePlaceholder),
      deleteMapping: t(CDP_STRINGS.attributeDeleteMapping),
      eventDetails: t(CDP_STRINGS.eventDetails),
      eventNameLabel: t(CDP_STRINGS.eventBuilderNameLabel),
      eventNamePlaceholder: t(CDP_STRINGS.eventBuilderNamePlaceholder),
      eventSourceNote: t(CDP_STRINGS.eventSourceNote),
      headerAttributeMapping: t(CDP_STRINGS.attributeMapping),
      requiredSection: t(CDP_STRINGS.requiredSection),
      saveChanges: t(CDP_STRINGS.saveChanges),
      sourceLabel: t(CDP_STRINGS.sourceLabel),
      tableHeaderFrom: t(CDP_STRINGS.attributeTableHeaderFrom),
      tableHeaderTo: t(CDP_STRINGS.attributeTableHeaderTo),
      toastEventSuccessAddedMessage: t(CDP_STRINGS.toastEventSuccessAddedMessage),
      toastEventSuccessAddedMessageEnd: t(CDP_STRINGS.toastEventSuccessAddedMessageEnd),
      toastEventSuccessAddedTitle: t(CDP_STRINGS.toastEventSuccessAddedTitle),
      toastEventSuccessUpdatedTitle: t(CDP_STRINGS.toastEventSuccessUpdatedTitle),
      uniqueIdentifierAttribute: t(CDP_STRINGS.attributeUniqueIdentifier),
      uniqueIdentifierAttributeNote: t(CDP_STRINGS.attributeUniqueIdentifierNote),
    };
  }, [t]);

  const errorLabels = useMemo(() => {
    return {
      attributeFormat: t(CDP_STRINGS.errorAttributeFormat),
      collectionFormat: t(CDP_STRINGS.errorCollectionNameFormat),
      requiredAttribute: t(CDP_STRINGS.errorRequiredAttribute),
      requiredCollection: t(CDP_STRINGS.errorRequiredCollection),
      requiredCollectionUnique: t(CDP_STRINGS.errorRequiredCollectionUnique),
      requiredDataService: t(CDP_STRINGS.errorRequiredDataService),
      requiredEventName: t(CDP_STRINGS.errorRequiredEventName),
      toastErrorLabel: t(CDP_STRINGS.toastErrorLabel),
      toastErrorTitle: t(CDP_STRINGS.toastErrorTitle),
      toastEventFailAddedMessage: t(CDP_STRINGS.toastEventFailAddedMessage),
      toastEventFailAddedMessageEnd: t(CDP_STRINGS.toastEventFailAddedMessageEnd),
      toastEventFailDeletedMessage: t(CDP_STRINGS.toastEventFailDeletedMessage),
      toastEventFailUpdatedMessageEnd: t(CDP_STRINGS.toastEventFailUpdatedMessageEnd),
      uniqueAttribute: t(CDP_STRINGS.errorAttributeUnique),
    };
  }, [t]);

  // Datastore instances.
  const datastoreInstances = useInstances(ConsoleInstanceType.datastore) as Array<EntityConsoleDatastoreInstance>;
  let blueprintInstances: Array<EntityConsoleDatastoreInstance> = [];
  if (datastoreInstances && datastoreInstances.length > 0) {
    blueprintInstances = datastoreInstances.filter((instance: EntityConsoleDatastoreInstance): boolean => {
      return instance.deploymentUrl.startsWith(`https`);
    });
  }

  /**
   * Get a validation response as an object.
   * @param {string} fieldID The field id (or error object key).
   * @param {string | null} errorMessage String error message for failures, or null for no error state.
   * @returns {EventBuilderError} An event builder error that can be used to update internal error state.
   */
  const getValidationResponse = useCallback((fieldId: string, errorMessage: string | null): EventBuilderError => {
    return { [fieldId]: errorMessage };
  }, []);

  /**
   * Does field validation on a given value and set of validators.
   * @param {string} value The value to check.
   * @param {Array<AnyValidator>} validationMap An array of validators to use.
   * @returns {EventBuilderFieldValidationResponse} Validation responses including error messages and validation state.
   */
  const doFieldValidation = useCallback(
    (value: string, validationMap: Array<AnyValidator>): EventBuilderFieldValidationResponse => {
      const messages = validationMap
        .map((v) => v.validate(value) as EventBuilderError)
        .reduce((prev, curr) => {
          const entries = Object.entries(curr);
          const [key, message] = entries[0];
          if (!has(prev, key) || (isString(message) && !isString(prev[key]))) {
            prev[key] = message;
          }
          return prev;
        }, {});
      return {
        isValid: !some(Object.values(messages)),
        messages,
      };
    },
    [],
  );

  // Validation instances.
  const fieldValidations = useMemo((): Record<string, Array<AnyValidator> | ((a: string) => Array<AnyValidator>)> => {
    const uniqueCollectionValidator = new UniqueValueValidator<object | null>(
      () => getValidationResponse(`collection`, errorLabels.requiredCollectionUnique),
      () => getValidationResponse(`collection`, null),
    );
    uniqueCollectionValidator.setAllowedCount(0);
    uniqueCollectionValidator.setValues(otherDatastoreInsights, [``]);
    uniqueCollectionValidator.setValueModifier((v) => {
      return `${event.filter.datastoreURL}${v}`;
    });
    const uniqueDataServiceCollectionValidator = new UniqueValueValidator<object | null>(
      () => getValidationResponse(`dataService`, errorLabels.requiredCollectionUnique),
      () => getValidationResponse(`dataService`, null),
    );
    uniqueDataServiceCollectionValidator.setAllowedCount(0);
    uniqueDataServiceCollectionValidator.setValues(otherDatastoreInsights, [``]);
    uniqueDataServiceCollectionValidator.setValueModifier((v) => {
      return `${v}${event.filter.collectionPrefix}`;
    });
    return {
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.collection]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`collection`, errorLabels.requiredCollection),
          () => getValidationResponse(`collection`, null),
        ),
        new DatastoreCollectionValidator<object | null>(
          () => getValidationResponse(`collection`, errorLabels.collectionFormat),
          () => getValidationResponse(`collection`, null),
        ),
        uniqueCollectionValidator,
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.dataService]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`dataService`, errorLabels.requiredDataService),
          () => getValidationResponse(`dataService`, null),
        ),
        uniqueDataServiceCollectionValidator,
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.uniqueIdentifier]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`uniqueid`, errorLabels.requiredAttribute),
          () => getValidationResponse(`uniqueid`, null),
        ),
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.name]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`name`, errorLabels.requiredEventName),
          () => getValidationResponse(`name`, null),
        ),
      ],
      // 'Dynamic' attribute to field validator.
      property: (errorKey: string): Array<AnyValidator> => {
        const propertyValidator = new RegExpValidator<object | null>(
          () => getValidationResponse(errorKey, errorLabels.attributeFormat),
          () => getValidationResponse(errorKey, null),
        );
        propertyValidator.setExpr(/^[a-zA-Z0-9\-_$]+$/);
        return [
          new NotEmptyValidator<object | null>(
            () => getValidationResponse(errorKey, errorLabels.requiredAttribute),
            () => getValidationResponse(errorKey, null),
          ),
          propertyValidator,
        ];
      },
      // 'Dynamic' attribute from field validator.
      propertyKey: (errorKey: string): Array<AnyValidator> => {
        const uniqueAttributeValidator = new UniqueValueValidator<object | null>(
          () => getValidationResponse(errorKey, errorLabels.uniqueAttribute),
          () => getValidationResponse(errorKey, null),
        );
        // Set the values to validate against.
        const values = Array.from(
          (document.getElementById(EVENT_BUILDER_TABLE_ID) as HTMLTableElement).querySelectorAll(`input[id^="from"]`),
        ).map((i) => (i as HTMLInputElement).value);
        // Set values excluding empty strings.
        uniqueAttributeValidator.setValues(values, [``]);

        const propertyValidator = new RegExpValidator<object | null>(
          () => getValidationResponse(errorKey, errorLabels.attributeFormat),
          () => getValidationResponse(errorKey, null),
        );
        propertyValidator.setExpr(/^[a-zA-Z0-9\-_$]+$/);
        return [
          new NotEmptyValidator<object | null>(
            () => getValidationResponse(errorKey, errorLabels.requiredAttribute),
            () => getValidationResponse(errorKey, null),
          ),
          uniqueAttributeValidator,
          propertyValidator,
        ];
      },
    };
  }, [
    errorLabels.attributeFormat,
    errorLabels.collectionFormat,
    errorLabels.requiredAttribute,
    errorLabels.requiredCollection,
    errorLabels.requiredCollectionUnique,
    errorLabels.requiredDataService,
    errorLabels.requiredEventName,
    errorLabels.uniqueAttribute,
    event.filter.collectionPrefix,
    event.filter.datastoreURL,
    getValidationResponse,
    otherDatastoreInsights,
  ]);

  /**
   * Handle the attribute value change.
   * @param {string} value The value that was changed.
   * @param {string} attributeId The internal id of the attribute.
   * @param {string} fieldId The value of the id attribute of the field.
   * @param {boolean} isKey Set to true when the value is a key (left hand side value).
   * @returns {void}
   */
  const updateAttributeValue = useCallback(
    (value: string, attributeId: string, fieldId: string, isKey = true): void => {
      let validatorMap;
      if (isKey) {
        // @ts-ignore.
        validatorMap = fieldValidations.propertyKey(fieldId);
      } else {
        // @ts-ignore.
        validatorMap = fieldValidations.property(fieldId);
      }
      const { messages, isValid } = doFieldValidation(value, validatorMap);
      setErrors({
        ...errors,
        ...messages,
      });
      if (isValid) {
        setAttributes(
          attributes.map(([k, v, id]) => {
            if (id === attributeId) {
              return isKey ? [value, v, attributeId] : [k, value, attributeId];
            }
            return [k, v, id];
          }),
        );
      }
    },
    [attributes, doFieldValidation, errors, fieldValidations],
  );

  /**
   * Validate and update a non-dynamic event value if valid.
   * @param {string} propertyKey The known property key.
   * @param {string} propertyValue The known property value.
   * @returns {void}
   */
  const updateEventValue = useCallback(
    (propertyKey: string, propertyValue: string): void => {
      const validationMap: Array<AnyValidator> = fieldValidations[propertyKey] as Array<AnyValidator>;
      const { messages, isValid } = doFieldValidation(propertyValue, validationMap);
      setErrors({
        ...errors,
        ...messages,
      });
      if (isValid) {
        setEvent(set({ ...event }, propertyKey, propertyValue));
      }
    },
    [doFieldValidation, errors, event, fieldValidations, setEvent],
  );

  /**
   * Triggers validation of all fields in the component.
   * Note: uses actual input values rather than event state.
   */
  const validateAllFields = useCallback((): boolean => {
    let nextErrors: EventBuilderError = {};
    const valid: Array<boolean> = [];

    // Find inputs for raw values.
    Object.entries(EVENT_BUILDER_DATA_FORM_IDS).forEach(([, id]): void => {
      const input = document.getElementById(id) as HTMLInputElement;
      const validationMap: Array<AnyValidator> = fieldValidations[
        EVENT_BUILDER_FORM_ID_PROPERTY_MAP[id]
      ] as Array<AnyValidator>;
      const { messages, isValid } = doFieldValidation(input.value, validationMap);
      nextErrors = {
        ...nextErrors,
        ...messages,
      };
      valid.push(isValid);
    });

    // Find attribute input values.
    const table = document.getElementById(EVENT_BUILDER_TABLE_ID) as HTMLTableElement;
    const inputs = Array.from(table.querySelectorAll(`tbody input`)) as Array<HTMLInputElement>;
    inputs.forEach((input) => {
      const validationMap: Array<AnyValidator> = input.id.includes(`from`)
        ? // @ts-ignore.
          fieldValidations.propertyKey(input.id)
        : // @ts-ignore.
          fieldValidations.property(input.id);
      const { messages, isValid } = doFieldValidation(input.value, validationMap);
      nextErrors = {
        ...nextErrors,
        ...messages,
      };
      valid.push(isValid);
    });

    // Completely replace state instead of appending it.
    setErrors(nextErrors);
    return every(valid);
  }, [doFieldValidation, fieldValidations]);

  /**
   * Adds a new blank attribute mapping.
   * @returns {void}
   */
  const addAttributeMapping = useCallback((): void => {
    setAttributes([...attributes, [``, ``, generateAttributeId()]]);
  }, [attributes, setAttributes]);

  /**
   * Delete attribute mapping (table row).
   * @param {string} attributeId The attribute field to delete.
   * @returns {void}
   */
  const deleteAttributeMapping = useCallback(
    (attributeId: string): void => {
      setAttributes(attributes.filter(([k, v, id]) => id !== attributeId));
      setErrors({
        ...errors,
        [`fromInput-${attributeId}`]: null,
        [`toInput-${attributeId}`]: null,
      });
    },
    [attributes, errors],
  );

  /**
   * Handles the save button click.
   */
  const saveClickHandler = useCallback(() => {
    // Success toast base props.
    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: isCreate ? labels.toastEventSuccessAddedTitle : labels.toastEventSuccessUpdatedTitle,
      toastType: TOAST_TYPE_SUCCESS,
    };

    // Error toast props
    const errorToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      toastContent: <strong>{errorLabels.toastErrorTitle}</strong>,
      toastType: TOAST_TYPE_DANGER,
    };

    // Validate all the values in one go.
    const isValid = validateAllFields();
    if (isValid) {
      // Construct the event to save.
      const eventToSave = {
        ...event,
        properties: attributes.reduce((prev: Object, curr: Array<string>) => {
          return {
            ...prev,
            [curr[0]]: curr[1],
          };
        }, {}),
      };
      setIsSaving(true);
      if (eventId) {
        lastValueFrom(insightsService.updateInsight({ insight: eventToSave, insightId: eventId }))
          .then(() => {
            const toastContent = undefined;
            const toastTitle = labels.toastEventSuccessUpdatedTitle;
            const toastProps = { ...successToastProps, title: toastTitle, toastContent };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastSuccessCreate} />);
            navigate({ pathname: getCapabilityPath(`/events`) });
            return true;
          })
          .finally((): void => {
            setIsSaving(false);
          })
          .catch(() => {
            const toastProps = {
              ...errorToastProps,
              toastContent: (
                <span>
                  {errorLabels.toastEventFailAddedMessage} <em>{eventToSave.name}</em>{' '}
                  {errorLabels.toastEventFailUpdatedMessageEnd}
                </span>
              ),
            };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastFailCreate} />);
            return true;
          });
      } else {
        lastValueFrom(insightsService.createInsight({ insight: eventToSave }))
          .then(() => {
            const toastContent = (
              <span>
                {labels.toastEventSuccessAddedMessage} <em>{eventToSave.name}</em>{' '}
                {labels.toastEventSuccessAddedMessageEnd}
              </span>
            );
            const toastTitle = labels.toastEventSuccessUpdatedTitle;
            const toastProps = { ...successToastProps, title: toastTitle, toastContent };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastSuccessCreate} />);
            navigate({ pathname: getCapabilityPath(`/events`) });
            return true;
          })
          .finally((): void => {
            setIsSaving(false);
          })
          .catch(() => {
            const toastProps = {
              ...errorToastProps,
              toastContent: <span>{errorLabels.toastErrorLabel}</span>,
            };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastFailCreate} />);
            return true;
          });
      }
    }
  }, [
    attributes,
    errorLabels.toastErrorLabel,
    errorLabels.toastErrorTitle,
    errorLabels.toastEventFailAddedMessage,
    errorLabels.toastEventFailUpdatedMessageEnd,
    event,
    eventId,
    insightsService,
    isCreate,
    labels.toastEventSuccessAddedMessage,
    labels.toastEventSuccessAddedMessageEnd,
    labels.toastEventSuccessAddedTitle,
    labels.toastEventSuccessUpdatedTitle,
    navigate,
    updateActiveToast,
    validateAllFields,
  ]);

  const tableRows: Array<object> = useMemo(
    () =>
      attributes.map(([propertyKey, propertyValue, attributeId]) => {
        return {
          from: (
            <div className="event-builder-datastore-form__attribute-mapping-cell">
              <TextField
                errorMessage={errors[`fromInput-${attributeId}`]}
                fieldLabel={labels.attributeTextfieldFrom}
                hideLabel
                id={`fromInput-${attributeId}`}
                key={`fromInput-${attributeId}`}
                name={`fromInput-${attributeId}`}
                onChange={(e): void => {
                  // @ts-ignore.
                  updateAttributeValue(e.target.value, attributeId, `fromInput-${attributeId}`, true);
                }}
                placeholder={labels.attributePlaceholder}
                required
                value={propertyKey as string}
              />
              <span className="event-builder-datastore-form__arrow-icon">{ICON_ARROW_CIRCLE_RIGHT}</span>
            </div>
          ),
          to: (
            <div className="event-builder-datastore-form__attribute-mapping-cell">
              <TextField
                errorMessage={errors[`toInput-${attributeId}`]}
                fieldLabel={labels.attributeTextfieldTo}
                hideLabel
                id={`toInput-${attributeId}`}
                key={`toInput-${attributeId}`}
                name={`toInput-${attributeId}`}
                onChange={(e): void => {
                  // @ts-ignore.
                  updateAttributeValue(e.target.value, attributeId, `toInput-${attributeId}`, false);
                }}
                placeholder={labels.attributePlaceholder}
                required
                value={propertyValue as string}
              />
              <div>
                <Button
                  format={BUTTON_FORMAT_TRANSPARENT}
                  icon={ICON_DELETE}
                  isDisabled={attributes.length <= 1}
                  label={labels.deleteMapping}
                  onClick={(e: SyntheticEvent): void => {
                    deleteAttributeMapping(attributeId as string);
                  }}
                  theme={BUTTON_THEME_DEFAULT}
                />
              </div>
            </div>
          ),
        };
      }),
    [
      attributes,
      deleteAttributeMapping,
      errors,
      labels.attributePlaceholder,
      labels.attributeTextfieldFrom,
      labels.attributeTextfieldTo,
      labels.deleteMapping,
      updateAttributeValue,
    ],
  );

  if (!datastoreInstances) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const dataServiceSelectProps: SelectFieldProps = {
    callback: (option: SelectFieldOption): void => {
      updateEventValue(EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.dataService], option.key);
    },
    defaultOption: blueprintInstances
      .filter((instance) => instance.deploymentUrl === event.filter.datastoreURL)
      .map((instance): SelectFieldOption => {
        return {
          key: instance.deploymentUrl,
          label: instance.name,
          value: instance.deploymentUrl,
        };
      })
      .pop(),
    errorMessage: errors.dataService,
    fieldLabel: labels.dataServiceSelectLabel,
    id: EVENT_BUILDER_DATA_FORM_IDS.dataService,
    name: `data-service`,
    options: blueprintInstances.map((instance): SelectFieldOption => {
      return {
        key: instance.deploymentUrl,
        label: instance.name,
        value: instance.deploymentUrl,
      };
    }),
    placeholder: labels.dataServiceSelectPlaceholder,
    required: true,
    value: event.filter.datastoreURL,
  };

  const sourceSelectProps: SelectFieldProps = {
    callback: (option: SelectFieldOption): void => {
      // Do nothing.
    },
    defaultOption: {
      key: `data-integration`,
      label: labels.dataIntegration,
      value: `data-integration`,
    },
    errorMessage: errors.source,
    fieldLabel: labels.sourceLabel,
    id: `event-source`,
    name: `source`,
    note: labels.eventSourceNote,
    options: [
      {
        key: `data-integration`,
        label: labels.dataIntegration,
        value: `data-integration`,
      },
    ],
    readOnly: true,
    required: true,
    value: ``,
  };

  const EventDetails = (
    <div className="event-builder-datastore-form__details">
      <div className="sds-row">
        <TextField
          data-id={EVENT_BUILDER_DATA_IDS.eventDetailsName}
          errorMessage={errors?.name}
          fieldLabel={labels.eventNameLabel}
          id={EVENT_BUILDER_DATA_FORM_IDS.name}
          name={EVENT_BUILDER_DATA_FORM_IDS.name}
          onChange={(e): void => {
            // @ts-ignore.
            updateEventValue(EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.name], e.target.value);
          }}
          placeholder={labels.eventNamePlaceholder}
          required
          value={event.name}
        />
        <SelectField {...sourceSelectProps} ref={null} />
        <SelectField {...dataServiceSelectProps} ref={dataServiceSelectRef} />
        <TextField
          data-id={EVENT_BUILDER_DATA_IDS.eventDetailsCollectionName}
          errorMessage={errors?.collection}
          fieldLabel={labels.collectionLabel}
          id={EVENT_BUILDER_DATA_FORM_IDS.collection}
          name={EVENT_BUILDER_DATA_FORM_IDS.collection}
          note={labels.collectionNote}
          onBlur={(e): void => {
            updateEventValue(
              EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.collection],
              // @ts-ignore.
              e.target.value,
            );
          }}
          placeholder={labels.collectionPlaceholder}
          required
          value={event.filter.collectionPrefix}
        />
        <TextField
          data-id={EVENT_BUILDER_DATA_IDS.eventUniqueIdentifier}
          errorMessage={errors.uniqueid}
          fieldLabel={labels.uniqueIdentifierAttribute}
          icon={ICON_VPN_KEY}
          id={EVENT_BUILDER_DATA_FORM_IDS.uniqueIdentifier}
          name={EVENT_BUILDER_DATA_FORM_IDS.uniqueIdentifier}
          note={labels.uniqueIdentifierAttributeNote}
          onChange={(e): void => {
            updateEventValue(
              EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_DATA_FORM_IDS.uniqueIdentifier],
              // @ts-ignore.
              e.target.value,
            );
          }}
          placeholder={labels.attributePlaceholder}
          required
          value={event.index.target}
        />
      </div>
    </div>
  );

  const tableColumns: Array<TableCell> = [
    {
      content: labels.tableHeaderFrom,
      key: `from`,
    },
    {
      content: labels.tableHeaderTo,
      key: `to`,
    },
  ];

  const AttributeMapping = (
    <div className="event-builder-datastore-form__attribute-mapping">
      <h2>
        {labels.headerAttributeMapping}
        <span className="sr-only">{labels.requiredSection}</span>
        <span>*</span>
      </h2>
      <div className="sds-row">
        <span className="events-builder-datastore-form__attributes-tableDescription">
          {labels.attributeMappingDescription}
        </span>
        <Table
          classes={`events-builder-datastore-form__attributes-table`}
          id={EVENT_BUILDER_TABLE_ID}
          isPaginated={false}
          tableContent={{ columns: tableColumns, rows: tableRows }}
        />
        <div className="event-builder-datastore-form__attribute-actions">
          <Button
            data-id={EVENT_BUILDER_DATA_IDS.eventBuilderAddAttribute}
            format={BUTTON_FORMAT_TRANSPARENT}
            icon={ICON_ADD}
            label={labels.addAttribute}
            onClick={addAttributeMapping}
            text={labels.addAttribute}
            theme={BUTTON_THEME_DEFAULT}
          />
        </div>
      </div>
    </div>
  );

  const actionButtons = [];

  if (!isSaving) {
    actionButtons.push(
      <Button
        format={BUTTON_FORMAT_SOFT}
        isLoading={isSaving}
        key="discard-changes"
        onClick={resetChanges}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />,
    );
  }

  actionButtons.push(
    <Button
      data-id={EVENT_BUILDER_DATA_IDS.buttonSaveChanges}
      isLoading={isSaving}
      key="save-changes"
      label={isCreate ? labels.addEvent : labels.saveChanges}
      onClick={saveClickHandler}
      text={isCreate ? labels.addEvent : labels.saveChanges}
    />,
  );

  return (
    <>
      {/* Note: this heading needs to exist outside of wrapping elements to get styled correctly */}
      <h2>{labels.eventDetails}</h2>
      <form className="event-builder-datastore-form">
        {EventDetails}
        {AttributeMapping}
        <div className="event-builder-datastore-form__actions">
          <ButtonList buttonKeys={[`discard-changes`, `save-changes`]} buttons={actionButtons} />
        </div>
      </form>
    </>
  );
};
